import type { FC } from 'react'
import React, { memo, useEffect, useState } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { ITheme } from '@homeviews/styles'
import ChartTooltip from '../ChartTooltip'
import ChartLegend from '../ChartLegend'
import {
  CHART_BRAND_COLORS,
  DEVELOPMENT_SCORES_CATEGORY_OPTIONS,
} from '../../helpers/settings'
import {
  chartTheme,
  truncateDevelopmentScoresLabel,
} from '../../helpers/layout'
import { transformSingleDevelopmentAdditionalData } from '../../helpers/transform'
import NoData from '../NoData'
import CustomBar from '../CustomBar'
import { isEqual } from '@homeviews/utils'
import { Select } from '@homeviews/shared-ui'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      '& .l-chartWrapper': {
        height: 355,
      },
    },
    bottomAxis: {
      display: 'flex',
      justifyContent: 'space-between',
      marginLeft: 200,
      marginTop: 5,
      '& span': {
        color: theme.colors.grey450,
        fontSize: 12,
        lineHeight: '15px',
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    selectWrapper: {
      minWidth: 200,
      fontWeight: 500,
    },
  }),
)
interface IProps {
  data: any
  developmentsAverageData: any
  developmentsBenchmarkData: any
  gridRef?: HTMLElement
  singleDevelopmentScoresChart?: boolean
  filtersDetails?: any
  developmentType?: any
}

const BenchmarkLayer =
  ({ xValue, singleDevData }) =>
  (layerProps) => {
    if (singleDevData && layerProps?.bars?.length) {
      return layerProps.bars.map((bar, index: number) => {
        return (
          <g key={bar.key}>
            <line
              x1={layerProps.xScale(singleDevData[index])}
              x2={layerProps.xScale(singleDevData[index])}
              y1={bar.y - 12}
              y2={bar.y + 28}
              fill="none"
              stroke={CHART_BRAND_COLORS.gold2}
            />
          </g>
        )
      })
    } else {
      return (
        <g>
          <circle
            cx={layerProps.xScale(xValue)}
            cy={10}
            r="6"
            fill={CHART_BRAND_COLORS.gold2}
          />
          <line
            x1={layerProps.xScale(xValue)}
            x2={layerProps.xScale(xValue)}
            y1={10}
            y2={layerProps.innerHeight}
            fill="none"
            stroke={CHART_BRAND_COLORS.gold2}
          />
        </g>
      )
    }
  }

const AverageLayer =
  ({ xValue, singleDevData }) =>
  (layerProps) => {
    if (singleDevData && layerProps?.bars?.length) {
      return layerProps.bars.map((bar, index: number) => {
        return (
          <g key={bar.key}>
            <line
              x1={layerProps.xScale(singleDevData[index])}
              x2={layerProps.xScale(singleDevData[index])}
              y1={bar.y - 12}
              y2={bar.y + 28}
              fill="none"
              stroke={CHART_BRAND_COLORS.darkBlue3}
            />
          </g>
        )
      })
    } else {
      return (
        <g>
          <circle
            cx={layerProps.xScale(xValue)}
            cy={10}
            r="6"
            fill={CHART_BRAND_COLORS.darkBlue3}
          />
          <line
            x1={layerProps.xScale(xValue)}
            x2={layerProps.xScale(xValue)}
            y1={10}
            y2={layerProps.innerHeight}
            fill="none"
            stroke={CHART_BRAND_COLORS.darkBlue3}
          />
        </g>
      )
    }
  }

const SCALE_FACTOR = 25

const DevelopmentScoresChart: FC<IProps> = memo(
  ({
    data,
    developmentsAverageData,
    developmentsBenchmarkData,
    gridRef,
    singleDevelopmentScoresChart,
    filtersDetails,
    developmentType,
  }) => {
    const classes = useStyles()
    const [filteredData, setFilteredData] = useState<any>(data)
    const [accessor, setAccessor] = useState<string | number>('rating')

    useEffect(() => {
      if (!data || !data?.length) {
        return
      }
      if (singleDevelopmentScoresChart) {
        setFilteredData(data)
        return
      }
      const newData = data
        .filter((item: any) => {
          return Number(item[accessor])
        })
        .map((item: any, i: number) => ({
          ...item,
          indexAndId: `${i}_${item.id}`,
          value: item[accessor] || 0,
        }))

      setFilteredData(newData)
    }, [data, accessor])

    let height = filteredData.length * SCALE_FACTOR
    if (height < 350) {
      height = 350
    }

    if (!data || !data?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    const handleSelectChange = (option: {
      value: string | number
      label: 'string'
    }) => {
      setAccessor(option?.value || '')

      mixpanelTrack('DASHBOARD_CHART_FILTER_CHANGE', {
        'Chart Name': 'Development Scores',
        Filter: option?.label || '',
      })
    }

    const singleBenchmarkData = singleDevelopmentScoresChart
      ? transformSingleDevelopmentAdditionalData(
          developmentsBenchmarkData?.data,
        )
      : null
    const singleAverageData = singleDevelopmentScoresChart
      ? transformSingleDevelopmentAdditionalData(developmentsAverageData?.data)
      : null

    let benchmarkTooltipText =
      'Discover the HomeViews UK benchmark. This is based on all time data up to the last full half year period.'

    if (developmentType === 'Build To Rent') {
      benchmarkTooltipText =
        'Discover the HomeViews UK benchmark specific to Build to Rent. This is based on all time data up to the last full half year period.'
    } else if (developmentType === 'Build To Sell') {
      benchmarkTooltipText =
        'Discover the HomeViews UK benchmark specific, based upon reviews in Build to Sell properties. This is based on all time data up to the last full half year period.'
    }

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          {filtersDetails && filtersDetails}
          {!singleDevelopmentScoresChart && (
            <div className={classes.selectWrapper}>
              <Select
                options={DEVELOPMENT_SCORES_CATEGORY_OPTIONS}
                value={accessor}
                onChange={handleSelectChange}
                alignRight
              />
            </div>
          )}
        </div>

        <div className="l-chartWrapper l-chartWrapper--scrollable">
          <div
            className="l-chartWrapper__inner"
            style={{
              height,
            }}
          >
            {/* Documentation: https://nivo.rocks/bar/ */}
            <ResponsiveBar
              data={filteredData}
              indexBy={singleDevelopmentScoresChart ? 'id' : 'indexAndId'}
              indexScale={{ type: 'band', round: false }}
              theme={{
                ...chartTheme,
                textColor: '#000433',
                fontSize: 12,
              }}
              layout="horizontal"
              barComponent={(barProps) => (
                <CustomBar {...barProps} barLayout="developmentScores" />
              )}
              animate={true}
              padding={0.7}
              borderRadius={5}
              colors={[CHART_BRAND_COLORS.green1]}
              margin={{ top: 0, right: 0, bottom: 0, left: 200 }}
              enableLabel={false}
              maxValue={5}
              axisBottom={null}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                format: (value: string) => {
                  if (value !== undefined) {
                    if (singleDevelopmentScoresChart || !value.includes('_'))
                      return value

                    const strParts = value.split('_') // Start with indexAndId="1_Fizzy Poplar, E14"
                    strParts.shift()
                    const id = strParts.join('_') // End with e.g. id="Fizzy Poplar, E14"
                    return truncateDevelopmentScoresLabel(id) // Truncate to 30 characters if longer
                  }
                },
              }}
              layers={[
                'grid',
                'axes',
                'bars',
                'markers',
                'legends',
                'annotations',
                typeof developmentsBenchmarkData?.data?.[accessor] !==
                'undefined'
                  ? BenchmarkLayer({
                      xValue: developmentsBenchmarkData?.data?.[accessor] || 0,
                      singleDevData: singleBenchmarkData,
                    })
                  : null,
                AverageLayer({
                  xValue: developmentsAverageData?.data?.[accessor] || 0,
                  singleDevData: singleAverageData,
                }),
              ]}
              tooltip={(tooltip) => {
                return (
                  <div
                    style={{
                      transform:
                        tooltip.index >= 2
                          ? `translateY(100%)`
                          : `translateY(0%)`,
                    }}
                  >
                    <ChartTooltip>
                      <ChartLegend
                        direction="vertical"
                        data={[
                          {
                            text: `${tooltip.data?.id}: ${tooltip.data?.value}`,
                            shapeColor: tooltip.color,
                          },
                          {
                            text: `Portfolio Average Rating: ${
                              singleDevelopmentScoresChart
                                ? singleAverageData?.[tooltip.index]
                                : developmentsAverageData?.data?.[accessor] || 0
                            }`,
                            shapeColor: CHART_BRAND_COLORS.darkBlue3,
                          },
                          typeof developmentsBenchmarkData?.data?.[accessor] !==
                          'undefined'
                            ? {
                                text: `Benchmark Rating: ${
                                  singleDevelopmentScoresChart
                                    ? singleBenchmarkData?.[tooltip.index]
                                    : developmentsBenchmarkData?.data?.[
                                        accessor
                                      ]
                                }${
                                  developmentType ? ` (${developmentType})` : ''
                                }`,
                                shapeColor: CHART_BRAND_COLORS.gold2,
                              }
                            : null,
                        ]}
                      />
                    </ChartTooltip>
                  </div>
                )
              }}
            />
          </div>
        </div>
        <div className={classes.bottomAxis}>
          {Array(6)
            .fill('0')
            .map((el, i) => {
              return <span key={i}>{i}</span>
            })}
        </div>
        {singleDevelopmentScoresChart ? (
          <ChartLegend
            data={[
              {
                text: 'Development Rating',
                shapeColor: CHART_BRAND_COLORS.green1,
              },
              {
                text: `Portfolio Average Rating`,
                shapeColor: CHART_BRAND_COLORS.darkBlue3,
              },
              {
                text: `Benchmark Rating ${
                  developmentType ? ` (${developmentType})` : ''
                }`,
                shapeColor: CHART_BRAND_COLORS.gold2,
              },
            ]}
          />
        ) : (
          <ChartLegend
            data={[
              {
                text: 'Development Rating',
                shapeColor: CHART_BRAND_COLORS.green1,
              },
              {
                text: `Portfolio Average Rating`,
                shapeColor: CHART_BRAND_COLORS.darkBlue3,
              },
              typeof developmentsBenchmarkData?.data?.[accessor] !== 'undefined'
                ? {
                    text: `Benchmark Rating ${
                      developmentType ? ` (${developmentType})` : ''
                    }`,
                    shapeColor: CHART_BRAND_COLORS.gold2,
                    tooltipText: benchmarkTooltipText,
                  }
                : null,
            ]}
          />
        )}
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default DevelopmentScoresChart
